import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Uploader from "@/components/Uploader";
import { api, formatIDR, formatApiErrorDetail, fileUrl } from "@/lib/api";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";

export default function Register() {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", gender: "male",
    age: "", jersey_size: "", jersey_design: "", city: "", shipping_address: "", emergency_contact: "",
    category_id: "", region_id: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [config, setConfig] = useState({});
  const [created, setCreated] = useState(null);
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    api.get("/events").then((r) => {
      const evs = (r.data || []).filter((e) => e.is_active);
      setEvents(evs);
      const pre = searchParams.get("event");
      const found = evs.find((e) => e.id === pre);
      if (found) setSelectedEvent(found);
      else if (evs.length === 1) setSelectedEvent(evs[0]);
    }).catch(() => {});
    api.get("/config").then((r) => setConfig(r.data));
  }, []);

  const [destinations, setDestinations] = useState([]);
  const [destQuery, setDestQuery] = useState("");
  const [shipChoice, setShipChoice] = useState(null);

  const normCity = (s) => {
    let v = String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    for (const p of ["kabupaten", "kota", "kab"]) if (v.startsWith(p)) { v = v.slice(p.length); break; }
    return v;
  };

  const chooseDestination = (value) => {
    setDestQuery(value);
    const m = destinations.find((d) => normCity(d.city_name) === normCity(value));
    setShipChoice(m ? { courier: "pos", service: "REGULER", cost: m.fee_idr, description: "POS Indonesia Reguler", city_name: m.city_name } : null);
  };

  const [destLoaded, setDestLoaded] = useState(false);
  useEffect(() => {
    if (!selectedEvent) return;
    setDestLoaded(false);
    setDestinations([]);
    setDestQuery("");
    setShipChoice(null);
    setForm((f) => ({ ...f, category_id: "", jersey_design: "" }));
    api.get(`/categories?event_id=${selectedEvent.id}`).then((r) => setCategories(r.data.filter((c) => c.is_active)));
    api.get(`/shipping/destinations?event_id=${selectedEvent.id}`)
      .then((res) => setDestinations(res.data.destinations || []))
      .catch(() => toast.error("Gagal memuat daftar kota"))
      .finally(() => setDestLoaded(true));
  }, [selectedEvent]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return toast.error("Pilih event terlebih dahulu");
    if (!form.category_id) return toast.error("Pilih kategori lari");
    if (destinations.length > 0 && !shipChoice) return toast.error("Pilih kota tujuan pengiriman dari daftar");
    setSubmitting(true);
    try {
      const payload = { ...form, age: parseInt(form.age), event_id: selectedEvent.id };
      if (destinations.length > 0 && shipChoice) {
        payload.shipping_courier = shipChoice.courier;
        payload.shipping_service = shipChoice.service;
        payload.destination_city_name = shipChoice.city_name;
      }
      const { data } = await api.post("/registrations", payload);
      setCreated(data);
      toast.success("Pendaftaran berhasil! Lanjutkan ke pembayaran.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal mendaftar");
    } finally {
      setSubmitting(false);
    }
  };

  if (created) return <PaymentInstructions reg={created} config={config} onDone={() => nav(`/dashboard?email=${encodeURIComponent(created.email)}&phone=${encodeURIComponent(created.phone)}`)} />;

  if (!selectedEvent) return (
    <div className="min-h-screen">
      <Nav />
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12" data-testid="event-picker">
        <div className="text-[10px] tracking-[0.3em] text-[#FF3B30] font-display">STEP 01</div>
        <h1 className="font-display text-5xl mt-3">PILIH EVENT</h1>
        <p className="mt-2 text-white/60 text-sm">Pilih event yang ingin kamu ikuti.</p>
        <div className="grid sm:grid-cols-2 gap-4 mt-8">
          {events.map((ev) => (
            <button key={ev.id} onClick={() => setSelectedEvent(ev)} className="card-flat p-6 text-left hover:border-[#FF3B30] transition-colors" data-testid={`pick-event-${ev.id}`}>
              <div className="font-display text-2xl tracking-wide">{ev.name}</div>
              {ev.description && <div className="mt-2 text-sm text-white/60">{ev.description}</div>}
            </button>
          ))}
        </div>
        {events.length === 0 && <div className="mt-8 text-white/50">Belum ada event aktif.</div>}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen relative z-10">
      {selectedEvent.leaderboard_background_path && (
        <div className="fixed inset-0 -z-10" data-testid="register-event-bg">
          <img src={fileUrl(selectedEvent.leaderboard_background_path)} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/75" />
        </div>
      )}
      <Nav />
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        <div className="text-[10px] tracking-[0.3em] text-[#FF3B30] font-display">STEP 01</div>
        <h1 className="font-display text-5xl mt-3">REGISTER<span className="text-[#FF3B30]">.</span></h1>
        <div className="mt-2 flex items-center gap-3">
          <span className="text-[#FF3B30] font-display tracking-widest" data-testid="register-event-name">{selectedEvent.name}</span>
          {events.length > 1 && (
            <button type="button" onClick={() => setSelectedEvent(null)} className="text-xs text-white/60 hover:text-[#FF3B30] underline underline-offset-2" data-testid="change-event-btn">GANTI EVENT</button>
          )}
        </div>
        <p className="mt-2 text-white/60 text-sm">Isi form di bawah. Data ini akan tercetak di e-BIB dan e-sertifikat.</p>

        <form onSubmit={submit} className="mt-10 grid md:grid-cols-2 gap-6" data-testid="register-form">
          <Field label="Full Name" required>
            <input required data-testid="reg-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="tactical-input" placeholder="e.g. Budi Santoso" />
          </Field>
          <Field label="Email" required>
            <input required type="email" data-testid="reg-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="tactical-input" placeholder="you@email.com" />
          </Field>
          <Field label="Phone (WhatsApp)" required>
            <input required type="tel" inputMode="numeric" autoComplete="tel" data-testid="reg-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="tactical-input" placeholder="0812xxxxxxxx" />
          </Field>
          <Field label="Gender" required>
            <select required data-testid="reg-gender" value={form.gender} onChange={(e) => set("gender", e.target.value)} className="tactical-input">
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </Field>
          <Field label="Usia (Tahun)" required>
            <input required type="number" min="5" max="100" data-testid="reg-age" value={form.age} onChange={(e) => set("age", e.target.value)} className="tactical-input" placeholder="e.g. 28" />
          </Field>
          <Field label="City" required>
            <input required data-testid="reg-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="tactical-input" placeholder="Jakarta" />
          </Field>
          <Field label="Emergency Contact (optional)">
            <input data-testid="reg-emergency" value={form.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} className="tactical-input" placeholder="Name / phone" />
          </Field>
          <Field label="Category" required>
            <select required data-testid="reg-category" value={form.category_id} onChange={(e) => set("category_id", e.target.value)} className="tactical-input">
              <option value="">-- Choose --</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{`${c.name} · ${c.distance_km} KM · ${formatIDR(c.price_idr)}`}</option>
              ))}
            </select>
          </Field>
          {destLoaded && destinations.length > 0 ? (
            <div className="md:col-span-2">
              <div className="text-[10px] tracking-widest text-white/60 uppercase font-display mb-2">
                Kota / Kabupaten Tujuan Kirim <span className="text-[#FF3B30]">*</span>
              </div>
              <input
                list="dest-list"
                required
                data-testid="reg-destination"
                value={destQuery}
                onChange={(e) => chooseDestination(e.target.value)}
                className="tactical-input"
                placeholder="Ketik nama kota, mis. Jakarta Pusat"
                autoComplete="off"
              />
              <datalist id="dest-list">
                {destinations.map((d) => <option key={d.city_key} value={d.city_name} />)}
              </datalist>
              {shipChoice && (
                <div className="mt-3 flex items-center justify-between gap-4 p-3 border border-[#FF3B30] bg-[#FF3B30]/10" data-testid="courier-option-0">
                  <div>
                    <div className="font-display tracking-widest">POS · REGULER</div>
                    <div className="text-xs text-white/50">POS Indonesia Reguler → {shipChoice.city_name}</div>
                  </div>
                  <div className="font-mono-num text-lg text-[#FF3B30]">{formatIDR(shipChoice.cost)}</div>
                </div>
              )}
              {destQuery && !shipChoice && !destinations.some((d) => d.city_name.toLowerCase().includes(destQuery.toLowerCase())) && (
                <div className="mt-2 text-xs text-amber-400" data-testid="dest-not-found">Kota tidak ditemukan dalam tabel ongkir. Pilih dari daftar yang muncul.</div>
              )}
            </div>
          ) : (
            (config.regions || []).length > 0 && (
              <Field label="Wilayah Pengiriman Race Pack" required>
                <select required data-testid="reg-region" value={form.region_id} onChange={(e) => set("region_id", e.target.value)} className="tactical-input">
                  <option value="">-- Choose --</option>
                  {(config.regions || []).map((r) => (
                    <option key={r.id} value={r.id}>{`${r.name}${r.fee_idr > 0 ? ` · +${formatIDR(r.fee_idr)}` : " · FREE"}`}</option>
                  ))}
                </select>
              </Field>
            )
          )}
          <div className="md:col-span-2">
            {config.size_chart_path && (
              <img src={fileUrl(config.size_chart_path)} alt="Size Chart Ukuran Baju" className="w-full max-w-md border border-[#27272A] mb-3" data-testid="size-chart-img" />
            )}
            <Field label="Size Ukuran Baju" required>
              <select required data-testid="reg-size" value={form.jersey_size} onChange={(e) => set("jersey_size", e.target.value)} className="tactical-input">
                <option value="">-- Choose --</option>
                {["XS", "S", "M", "L", "XL", "XXL", "3XL"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            {(selectedEvent.jersey_designs || []).length > 0 && (
              <div className="mt-4">
                <Field label="Pilih Design" required>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="reg-design">
                    {(selectedEvent.jersey_designs || []).map((raw) => {
                      const d = typeof raw === "string" ? { name: raw, image_path: "" } : raw;
                      const sel = form.jersey_design === d.name;
                      return (
                        <button
                          type="button"
                          key={d.name}
                          onClick={() => set("jersey_design", d.name)}
                          data-testid={`reg-design-${d.name}`}
                          className={`border text-left transition-colors ${sel ? "border-[#FF3B30] bg-[#FF3B30]/10" : "border-white/15 hover:border-white/40"}`}
                        >
                          {d.image_path ? (
                            <img src={fileUrl(d.image_path)} alt={d.name} className="w-full aspect-[4/5] object-cover" />
                          ) : (
                            <div className="w-full aspect-[4/5] bg-white/5 flex items-center justify-center text-white/30 text-xs tracking-widest">NO IMAGE</div>
                          )}
                          <div className="p-2 text-xs font-display tracking-widest flex items-center justify-between">
                            <span>{d.name.toUpperCase()}</span>
                            {sel && <span className="text-[#FF3B30]">DIPILIH</span>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </Field>
              </div>
            )}
          </div>
          <div className="md:col-span-2">
            <label className="block">
              <div className="text-[10px] tracking-widest text-white/60 uppercase font-display mb-2">
                Alamat Pengiriman Race Pack (Jersey & Medali) <span className="text-[#FF3B30]">*</span>
              </div>
              <textarea required rows={3} data-testid="reg-address" value={form.shipping_address} onChange={(e) => set("shipping_address", e.target.value)} className="tactical-input" placeholder="Jalan, nomor rumah, RT/RW, kelurahan, kecamatan, kota, kode pos" />
            </label>
          </div>
          <div className="md:col-span-2 flex items-center justify-between mt-4">
            <div className="text-xs text-white/50 tracking-widest">DATA IS FINAL — DOUBLE CHECK</div>
            <button type="submit" disabled={submitting} className="btn-blaze inline-flex items-center gap-2" data-testid="reg-submit">
              {submitting ? "SUBMITTING..." : (<>CONTINUE <ArrowRight size={18} weight="bold" /></>)}
            </button>
          </div>
          <div className="md:col-span-2 text-xs text-white/50 flex items-start gap-2" data-testid="reg-dashboard-note">
            <CheckCircle size={14} color="#34D399" className="mt-0.5 shrink-0" />
            <span>Setelah submit data, Anda langsung bisa masuk ke <span className="text-white/80 font-display tracking-wide">MY DASHBOARD</span> kapan saja menggunakan email &amp; nomor HP yang didaftarkan — untuk cek status pembayaran, unduh e-BIB, dan submit hasil lari.</span>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-widest text-white/60 uppercase font-display mb-2">
        {label} {required && <span className="text-[#FF3B30]">*</span>}
      </div>
      {children}
    </label>
  );
}

function PaymentInstructions({ reg, config, onDone }) {
  const [proofPath, setProofPath] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!proofPath) return toast.error("Unggah bukti transfer terlebih dahulu");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("path", proofPath);
      await api.post(`/registrations/${reg.id}/payment-proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bukti transfer diterima. Menunggu verifikasi tim kami.");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal mengunggah");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="min-h-screen">
      <Nav />
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12" data-testid="payment-page">
        <div className="text-[10px] tracking-[0.3em] text-[#FF3B30] font-display">STEP 02</div>
        <h1 className="font-display text-5xl mt-3">MAKE PAYMENT</h1>
        <div className="mt-2 text-white/60 text-sm">Registrasi tersimpan · {reg.event_name} · BIB sementara: <span className="font-mono-num text-[#FF3B30]">#{reg.bib_number}</span></div>

        <div className="mt-5 border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-4 flex items-start gap-3" data-testid="dashboard-access-note">
          <CheckCircle size={18} color="#FF3B30" className="mt-0.5 shrink-0" />
          <div className="text-sm text-white/80">
            <span className="font-display tracking-widest text-[#FF3B30] text-xs">AKSES MY DASHBOARD</span>
            <div className="mt-1">Data Anda sudah tersimpan. Mulai sekarang Anda sudah bisa masuk ke <span className="text-white font-display tracking-wide">MY DASHBOARD</span> kapan saja — cukup dengan <span className="text-white">email</span> dan <span className="text-white">nomor HP</span> yang didaftarkan. Di sana Anda bisa cek status pembayaran, unduh e-BIB, dan submit hasil lari.</div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mt-10">
          <div className="card-flat p-6">
            <div className="text-[10px] tracking-widest text-white/50 font-display">PAYMENT METHODS</div>
            {config.qris_image_path && (
              <div className="mt-4 pb-4 border-b border-white/10">
                <div className="text-xs tracking-widest text-white/50 font-display">OPTION 1 · QRIS</div>
                <img src={fileUrl(config.qris_image_path)} alt="QRIS Merchant" className="mt-3 w-full max-w-[280px] border border-[#27272A] bg-white p-2" data-testid="qris-image" />
                <div className="mt-2 text-xs text-white/50">Scan dengan aplikasi bank / e-wallet apa pun</div>
              </div>
            )}
            {config.bank_transfer_enabled !== false && (
              <div className="mt-4">
                <div className="text-xs tracking-widest text-white/50 font-display">{config.qris_image_path ? "OPTION 2 · BANK TRANSFER" : "BANK TRANSFER"}</div>
                <div className="font-display text-3xl mt-2">{config.bank_name}</div>
                <div className="mt-2 text-xs tracking-widest text-white/50">ACCOUNT NUMBER</div>
                <div className="font-mono-num text-2xl text-[#FF3B30]" data-testid="bank-account">{config.bank_account_number}</div>
                <div className="mt-2 text-xs tracking-widest text-white/50">HOLDER</div>
                <div className="font-display text-lg">{config.bank_account_holder}</div>
              </div>
            )}
            {!config.qris_image_path && config.bank_transfer_enabled === false && (
              <div className="mt-4 text-sm text-amber-400" data-testid="no-payment-method">Metode pembayaran belum tersedia. Silakan hubungi panitia.</div>
            )}
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="text-xs tracking-widest text-white/50">ENTRY FEE · {reg.category_name}</div>
              <div className="font-display text-xl">{formatIDR(reg.category_price_idr)}</div>
              <div className="mt-2 text-xs tracking-widest text-white/50">ONGKIR RACE PACK{reg.shipping_courier ? ` · ${reg.shipping_courier.toUpperCase()} ${reg.shipping_service || ""} → ${reg.destination_city_name || ""}` : (reg.region_name ? ` · ${reg.region_name.toUpperCase()}` : "")}</div>
              <div className="font-display text-xl">{reg.shipping_fee_idr ? formatIDR(reg.shipping_fee_idr) : "FREE"}</div>
              <div className="mt-2 text-xs tracking-widest text-white/50">KODE UNIK</div>
              <div className="font-display text-xl font-mono-num" data-testid="payment-unique-code">{String(reg.unique_code ?? 0).padStart(3, "0")}</div>
              <div className="mt-3 text-xs tracking-widest text-white/50">TOTAL TRANSFER</div>
              <div className="font-display text-3xl text-[#FF3B30]" data-testid="payment-total">{formatIDR(reg.total_transfer_idr ?? (reg.total_price_idr ?? reg.category_price_idr))}</div>
              <div className="text-xs text-amber-400/90 mt-2">Transfer TEPAT hingga 3 digit terakhir — kode unik membantu tim kami mencocokkan pembayaran Anda.</div>
              <div className="text-xs text-white/40 mt-2">BIB #{reg.bib_number}</div>
            </div>
          </div>
          <div className="card-flat p-6">
            <div className="text-[10px] tracking-widest text-white/50 font-display">UPLOAD PROOF</div>
            <div className="mt-4">
              <Uploader purpose="payment_proof" value={proofPath} onUploaded={setProofPath} label="Screenshot Bukti Transfer" testid="proof-upload" />
            </div>
            <button onClick={submit} disabled={submitting || !proofPath} className="btn-blaze w-full mt-6" data-testid="submit-proof-btn">
              {submitting ? "SUBMITTING..." : "SUBMIT PROOF"}
            </button>
            <div className="mt-4 text-xs text-white/50 flex items-start gap-2">
              <CheckCircle size={16} color="#34D399" className="mt-0.5" />
              <span>Verifikasi manual 1x24 jam. E-BIB akan aktif setelah pembayaran terverifikasi.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
